import Vue from "vue"

const state = {
    // Cache for gaps data per model/sectional/year
    gapsCache: {},

    // Cache for chronology checks
    chronologyCache: {},

    // Current chronology warnings being displayed
    currentWarning: null
}

const getters = {

    // Get current chronology warning
    getCurrentWarning: state => state.currentWarning,

    // Check if gaps exist for a specific model/sectional/year
    hasGaps: (state) => (modelType, sectionalId, year) => {
        const key = `${modelType}_${sectionalId}_${year}`
        const gaps = state.gapsCache[key]
        return gaps && gaps.enabled && gaps.total_missing > 0
    }
}

const mutations = {
    // Set gaps data for a model/sectional/year
    SET_GAPS: (state, { modelType, sectionalId, year, data }) => {
        const key = `${modelType}_${sectionalId}_${year}`
        Vue.set(state.gapsCache, key, data)
    },

    // Clear gaps cache for a specific model/sectional/year
    CLEAR_GAPS: (state, { modelType, sectionalId, year }) => {
        const key = `${modelType}_${sectionalId}_${year}`
        Vue.delete(state.gapsCache, key)
    },

    // Clear all gaps cache
    CLEAR_ALL_GAPS: (state) => {
        state.gapsCache = {}
    },

    // Set current chronology warning
    SET_CHRONOLOGY_WARNING: (state, warning) => {
        state.currentWarning = warning
    },

    // Clear current chronology warning
    CLEAR_CHRONOLOGY_WARNING: (state) => {
        state.currentWarning = null
    },

    // Cache chronology result
    SET_CHRONOLOGY_CACHE: (state, { key, data }) => {
        Vue.set(state.chronologyCache, key, {
            data,
            timestamp: Date.now()
        })
    },

    // Clear chronology cache
    CLEAR_CHRONOLOGY_CACHE: (state) => {
        state.chronologyCache = {}
    }
}

const actions = {
    /**
     * Fetch gaps for a specific model/sectional/year
     */
    async FETCH_GAPS({ commit }, { modelType, sectionalId, year }) {
        try {
            const response = await Vue.axios.get('/document-numbering/gaps', {
                params: { modelType, sectionalId, year }
            })

            if (response.data.success) {
                commit('SET_GAPS', {
                    modelType,
                    sectionalId,
                    year,
                    data: response.data.data
                })
                return response.data.data
            }
        } catch (error) {
            console.error('Error fetching gaps:', error)
            return null
        }
    },

    /**
     * Check chronology for a document
     */
    async CHECK_CHRONOLOGY({ commit }, { modelType, number, date, sectionalId, excludeId = null }) {
        try {
            const response = await Vue.axios.post('/document-numbering/chronology-check', {
                modelType,
                number,
                date,
                sectionalId,
                excludeId
            })

            if (response.data.success) {
                const result = response.data.data

                // Cache the result
                const key = `${modelType}_${sectionalId}_${number}_${date}`
                commit('SET_CHRONOLOGY_CACHE', { key, data: result })

                return result
            }
        } catch (error) {
            console.error('Error checking chronology:', error)
            return null
        }
    },

    /**
     * Get numbering statistics
     */
    async FETCH_STATS({ commit }, { modelType, sectionalId, year }) {
        try {
            const response = await Vue.axios.get('/document-numbering/stats', {
                params: { modelType, sectionalId, year }
            })

            if (response.data.success) {
                return response.data.data
            }
        } catch (error) {
            console.error('Error fetching stats:', error)
            return null
        }
    },

    /**
     * Get years with issues
     */
    async FETCH_YEARS_WITH_ISSUES({ commit }, { modelType, sectionalId, limit = 5 }) {
        try {
            const response = await Vue.axios.get('/document-numbering/years-with-issues', {
                params: { modelType, sectionalId, limit }
            })

            if (response.data.success) {
                return response.data.data
            }
        } catch (error) {
            console.error('Error fetching years with issues:', error)
            return null
        }
    },

    /**
     * Invalidate cache for a specific model/sectional/year
     */
    async INVALIDATE_CACHE({ commit }, { modelType, sectionalId, year }) {
        try {
            await Vue.axios.post('/document-numbering/invalidate-cache', {
                modelType,
                sectionalId,
                year
            })

            // Clear local cache as well
            commit('CLEAR_GAPS', { modelType, sectionalId, year })
        } catch (error) {
            console.error('Error invalidating cache:', error)
        }
    }
}

export default {
    state,
    getters,
    mutations,
    actions
}
